import type { EmployeeListItem, FilterStats, FilterType, PageConfig } from './types';

const createEmptyStats = (): FilterStats => ({
  allActiveCount: 0,
  traineeCount: 0,
  internalCount: 0,
  contractCount: 0,
  regularCount: 0,
  bpCount: 0,
  bpStudyCount: 0,
  bpEnrolledCount: 0,
  allTerminatedCount: 0,
  dropoutCount: 0,
  resignedCount: 0,
  bpFinishedCount: 0,
  actionRequiredCount: 0,
  birthdayLastMonthCount: 0,
  birthdayThisMonthCount: 0,
  birthdayNextMonthCount: 0,
  visaRenewal90daysCount: 0,
  regularInterviewWaitCount: 0,
  regularContractWaitCount: 0,
  traineeVisaRenewalCount: 0,
});

const toTodayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const toSafeDate = (value?: string) => {
  if (!value || !String(value).trim()) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getOneMonthBeforeSameDay = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const previousMonthLastDay = new Date(year, month, 0).getDate();
  const result = new Date(date);
  result.setFullYear(year, month - 1, Math.min(day, previousMonthLastDay));
  result.setHours(0, 0, 0, 0);
  return result;
};

const getRegularConversionNoticeStartDate = (row: EmployeeListItem) => {
  const contractPeriodTo = toSafeDate(row.contractPeriodTo);
  return contractPeriodTo ? getOneMonthBeforeSameDay(contractPeriodTo) : null;
};

export const normalizeCode = (value?: string | number | null) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim();
  if (!normalized) return '';
  if (/^\d+$/.test(normalized)) {
    return normalized.padStart(2, '0');
  }
  return normalized;
};

const normalizeStatus = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const matchesContractTypes = (row: EmployeeListItem, contractTypes: string[]) => {
  const contractType = normalizeCode(row.contractType);
  return contractTypes.includes(contractType);
};

export const isActiveAccount = (row: EmployeeListItem) => Number(row.dataDeleteFlag) !== 1;

const isRegularBirthdayTarget = (row: EmployeeListItem) => matchesContractTypes(row, ['03', '04']);

export const isActiveEmployee = (row: EmployeeListItem, today = toTodayStart()) => {
  const departureDate = toSafeDate(row.departureCompanyDate);
  return !departureDate || departureDate >= today;
};

export const isTerminatedEmployee = (row: EmployeeListItem, today = toTodayStart()) => {
  const departureDate = toSafeDate(row.departureCompanyDate);
  return !!departureDate && departureDate < today;
};

export const isBirthdayInMonth = (
  row: EmployeeListItem,
  monthType: 'last' | 'current' | 'next',
  today = toTodayStart(),
) => {
  const birthday = toSafeDate(row.birthday);
  if (!birthday) return false;

  const currentMonth = today.getMonth();
  let targetMonth = currentMonth;
  if (monthType === 'last') {
    targetMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  } else if (monthType === 'next') {
    targetMonth = currentMonth === 11 ? 0 : currentMonth + 1;
  }

  return birthday.getMonth() === targetMonth;
};

export const isVisaRenewal90Days = (row: EmployeeListItem, today = toTodayStart()) => {
  if (matchesContractTypes(row, ['05', '06'])) return false;
  const visaValidTo = toSafeDate(row.visaValidTo);
  if (!visaValidTo) return false;

  const deadline = new Date(today);
  deadline.setDate(deadline.getDate() + 90);
  return visaValidTo >= today && visaValidTo <= deadline;
};

export const isRegularInterviewWait = (row: EmployeeListItem, today = toTodayStart()) => {
  if (!matchesContractTypes(row, ['03'])) return false;
  const interviewStartDate = getRegularConversionNoticeStartDate(row);
  if (!interviewStartDate) return false;

  const regularConfirmStatus = normalizeStatus(row.regularConfirmStatus);
  return (
    today >= interviewStartDate &&
    (regularConfirmStatus === 0 || regularConfirmStatus === 2 || regularConfirmStatus === null)
  );
};

export const isRegularContractWait = (row: EmployeeListItem, today = toTodayStart()) => {
  if (!matchesContractTypes(row, ['03'])) return false;
  const contractStartDate = getRegularConversionNoticeStartDate(row);
  if (!contractStartDate) return false;

  return today >= contractStartDate && normalizeStatus(row.regularConfirmStatus) === 1;
};

export const isTraineeVisaRenewal = (row: EmployeeListItem, today = toTodayStart()) => {
  if (!matchesContractTypes(row, ['02'])) return false;
  const visaType = normalizeStatus(row.visaType);

  if ([7, 8, 9].includes(visaType ?? -1)) {
    return true;
  }

  if (visaType !== 1) {
    return false;
  }

  return isVisaRenewal90Days(row, today);
};

export const isBpFinished = (row: EmployeeListItem, today = toTodayStart()) => {
  if (!matchesContractTypes(row, ['05', '06'])) return false;

  const contractPeriodTo = toSafeDate(row.contractPeriodTo);
  return !!contractPeriodTo && contractPeriodTo < today;
};

export const isRelationshipEnded = (row: EmployeeListItem, today = toTodayStart()) => {
  return isTerminatedEmployee(row, today) || isBpFinished(row, today);
};

export const isActionRequired = (row: EmployeeListItem, today = toTodayStart()) => {
  if (isRelationshipEnded(row, today)) return false;

  if (isRegularBirthdayTarget(row) && isBirthdayInMonth(row, 'current', today)) return true;
  if (isVisaRenewal90Days(row, today)) return true;
  if (isRegularInterviewWait(row, today)) return true;
  if (isRegularContractWait(row, today)) return true;

  return false;
};

export const filterEmployeeList = (
  rows: EmployeeListItem[],
  selectedFilter: FilterType,
  pageConfig: PageConfig,
  today = toTodayStart(),
) => {
  const visibleRows = rows.filter(isActiveAccount);
  switch (selectedFilter) {
    case 'all_active':
      return visibleRows.filter((row) => !isRelationshipEnded(row, today));
    case 'all_terminated':
      return visibleRows.filter((row) => isRelationshipEnded(row, today));
    case '01':
      return visibleRows.filter(
        (row) => matchesContractTypes(row, ['01']) && !isRelationshipEnded(row, today),
      );
    case '02':
      return visibleRows.filter(
        (row) => matchesContractTypes(row, ['02']) && !isRelationshipEnded(row, today),
      );
    case '03':
      return visibleRows.filter(
        (row) => matchesContractTypes(row, ['03']) && !isRelationshipEnded(row, today),
      );
    case '04':
      return visibleRows.filter(
        (row) => matchesContractTypes(row, ['04']) && !isRelationshipEnded(row, today),
      );
    case '05':
      return visibleRows.filter(
        (row) => matchesContractTypes(row, ['05']) && !isRelationshipEnded(row, today),
      );
    case '06':
      return visibleRows.filter(
        (row) => matchesContractTypes(row, ['06']) && !isRelationshipEnded(row, today),
      );
    case 'dropout':
      return visibleRows.filter(
        (row) => matchesContractTypes(row, ['01', '02']) && isTerminatedEmployee(row, today),
      );
    case 'resigned':
      return visibleRows.filter(
        (row) => matchesContractTypes(row, ['03', '04']) && isTerminatedEmployee(row, today),
      );
    case 'bp_finished':
      return visibleRows.filter((row) => isBpFinished(row, today));
    case 'action_required':
      return pageConfig.showActionRequired
        ? visibleRows.filter((row) => isActionRequired(row, today))
        : [];
    case 'birthday_last_month':
      return pageConfig.showActionRequired
        ? visibleRows.filter(
            (row) =>
              !isRelationshipEnded(row, today) &&
              isRegularBirthdayTarget(row) &&
              isBirthdayInMonth(row, 'last', today),
          )
        : [];
    case 'birthday_this_month':
      return pageConfig.showActionRequired
        ? visibleRows.filter(
            (row) =>
              !isRelationshipEnded(row, today) &&
              isRegularBirthdayTarget(row) &&
              isBirthdayInMonth(row, 'current', today),
          )
        : [];
    case 'birthday_next_month':
      return pageConfig.showActionRequired
        ? visibleRows.filter(
            (row) =>
              !isRelationshipEnded(row, today) &&
              isRegularBirthdayTarget(row) &&
              isBirthdayInMonth(row, 'next', today),
          )
        : [];
    case 'visa_renewal_90days':
      return pageConfig.showActionRequired
        ? visibleRows.filter((row) => !isRelationshipEnded(row, today) && isVisaRenewal90Days(row, today))
        : [];
    case 'regular_interview_wait':
      return pageConfig.showActionRequired
        ? visibleRows.filter(
            (row) => !isRelationshipEnded(row, today) && isRegularInterviewWait(row, today),
          )
        : [];
    case 'regular_contract_wait':
      return pageConfig.showActionRequired
        ? visibleRows.filter(
            (row) => !isRelationshipEnded(row, today) && isRegularContractWait(row, today),
          )
        : [];
    case 'trainee_visa_renewal':
      return pageConfig.showActionRequired
        ? visibleRows.filter((row) => !isRelationshipEnded(row, today) && isTraineeVisaRenewal(row, today))
        : [];
    default:
      return [];
  }
};

export const calculateEmployeeFilterStats = (
  rows: EmployeeListItem[],
  pageConfig: PageConfig,
  today = toTodayStart(),
) => {
  const visibleRows = rows.filter(isActiveAccount);
  const activeRows = visibleRows.filter((row) => !isRelationshipEnded(row, today));
  const terminatedRows = visibleRows.filter((row) => isTerminatedEmployee(row, today));
  const endedRows = visibleRows.filter((row) => isRelationshipEnded(row, today));

  const stats = createEmptyStats();
  stats.allActiveCount = activeRows.length;
  stats.traineeCount = activeRows.filter((row) => matchesContractTypes(row, ['01'])).length;
  stats.internalCount = activeRows.filter((row) => matchesContractTypes(row, ['02'])).length;
  stats.contractCount = activeRows.filter((row) => matchesContractTypes(row, ['03'])).length;
  stats.regularCount = activeRows.filter((row) => matchesContractTypes(row, ['04'])).length;
  stats.bpCount = activeRows.filter((row) => matchesContractTypes(row, ['05', '06'])).length;
  stats.bpStudyCount = activeRows.filter((row) => matchesContractTypes(row, ['05'])).length;
  stats.bpEnrolledCount = activeRows.filter((row) => matchesContractTypes(row, ['06'])).length;

  stats.dropoutCount = terminatedRows.filter((row) =>
    matchesContractTypes(row, ['01', '02']),
  ).length;
  stats.resignedCount = terminatedRows.filter((row) =>
    matchesContractTypes(row, ['03', '04']),
  ).length;
  stats.bpFinishedCount = visibleRows.filter((row) => isBpFinished(row, today)).length;
  stats.allTerminatedCount = endedRows.length;

  stats.birthdayLastMonthCount = activeRows.filter(
    (row) => isRegularBirthdayTarget(row) && isBirthdayInMonth(row, 'last', today),
  ).length;
  stats.birthdayThisMonthCount = activeRows.filter(
    (row) => isRegularBirthdayTarget(row) && isBirthdayInMonth(row, 'current', today),
  ).length;
  stats.birthdayNextMonthCount = activeRows.filter(
    (row) => isRegularBirthdayTarget(row) && isBirthdayInMonth(row, 'next', today),
  ).length;
  stats.visaRenewal90daysCount = activeRows.filter((row) => isVisaRenewal90Days(row, today)).length;
  stats.regularInterviewWaitCount = activeRows.filter((row) =>
    isRegularInterviewWait(row, today),
  ).length;
  stats.regularContractWaitCount = activeRows.filter((row) =>
    isRegularContractWait(row, today),
  ).length;
  stats.traineeVisaRenewalCount = activeRows.filter((row) =>
    isTraineeVisaRenewal(row, today),
  ).length;

  stats.actionRequiredCount = pageConfig.showActionRequired
    ? stats.birthdayThisMonthCount +
      stats.visaRenewal90daysCount +
      stats.regularInterviewWaitCount +
      stats.regularContractWaitCount
    : 0;

  return stats;
};
